import cx from 'classnames'
import Button from 'components/Button'
import LoadingOverlay from 'components/LoadingOverlay'
import { useElementSize } from 'hooks/element-size'
import { useLoading } from 'hooks/loading-hooks'
import { observer } from 'mobx-react'
import * as React from 'react'
import { isChildrenEmpty } from 'utils/react-util'
import SubmissionViewModel from '../../../SubmissionViewModel'
import Billing, { AdminBilling } from './Billing'
import CouponSelection from './CouponSelection'
import Pricing from './Pricing'
import styles from './Submission.sass'
import SubmissionStatusModal from './SubmissionStatusModal'

interface SubmissionProps {
  vm: SubmissionViewModel
  shadow?: boolean
}

/**
 * When the box width is within this range, use the side-by-side layout.
 */
const SIDE_BY_SIDE_LAYOUT_BREAKPOINT = { min: 620, max: 750 }

/**
 * The Submission
 */
export default observer(function Submission({ vm, shadow }: SubmissionProps) {
  const loading = useLoading(vm.isLoading)
  const containerRef = React.useRef(null)
  const couponSelection = vm.billing.couponSelection
  const boxSize = useElementSize(containerRef)
  const billingRelevant = vm.billing.isBillingRelevant
  const shouldCollectPaymentInfo = vm.billing.shouldCollectPaymentInfo
  const shouldShowPricing = !!vm.summary?.pricing
  const couponSelected = billingRelevant && couponSelection.isCouponSelected
  const shouldRenderEmptyCouponSection =
    billingRelevant && couponSelection.canUseCoupons && !couponSelected
  const sideBySide =
    shouldCollectPaymentInfo &&
    shouldShowPricing &&
    boxSize.width < SIDE_BY_SIDE_LAYOUT_BREAKPOINT.max &&
    boxSize.width > SIDE_BY_SIDE_LAYOUT_BREAKPOINT.min
  const shouldRenderCouponSection = !vm.billing.canBypassBilling

  const billingSection = shouldCollectPaymentInfo ? (
    vm.billing.canBypassBilling ? (
      <AdminBilling vm={vm.billing} />
    ) : (
      <Billing vm={vm.billing} />
    )
  ) : null
  const pricingSection = <Pricing boxed={sideBySide} vm={vm} />

  // NOTE: The location of the coupon component in the tree differs
  // based on whether there is a selected coupon.
  const couponSection = shouldRenderCouponSection && couponSelected && (
    <CouponSelection vm={couponSelection} />
  )
  const emptyCouponSection = shouldRenderCouponSection &&
    shouldRenderEmptyCouponSection && <CouponSelection vm={couponSelection} />

  return (
    <SubmissionBox
      shadow={shadow}
      containerRef={containerRef}
      data-test={
        loading ? 'draft-editor-submission-loading' : 'draft-editor-submission'
      }
    >
      <Content>
        {sideBySide ? (
          <>
            <Column>
              {billingSection}
              {couponSection}
              {emptyCouponSection}
            </Column>
            <Column>{pricingSection}</Column>
          </>
        ) : (
          <Column>
            {billingSection}
            {couponSection}
            {pricingSection}
            {emptyCouponSection}
          </Column>
        )}
      </Content>
      <Spaced>
        <ConfirmButton vm={vm} />
        <Instructions vm={vm} />
      </Spaced>

      {loading && <LoadingOverlay />}
      <SubmissionStatusModal vm={vm.submissionStatus} />
    </SubmissionBox>
  )
})

interface SubmissionBoxProps {
  children?: React.ReactNode
  shadow?: boolean
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  ['data-test']?: string
}

/**
 " Submission Box
 */
function SubmissionBox({
  shadow,
  containerRef,
  children,
  'data-test': dataTest,
}: SubmissionBoxProps) {
  return (
    <div
      className={cx(styles.root, shadow && styles.shadow)}
      data-test={dataTest}
      ref={containerRef}
    >
      {children}
    </div>
  )
}

interface ContentProps {
  children?: React.ReactNode
}

/**
 " Content
 */
function Content({ children }: ContentProps) {
  return <div className={styles.content}>{children}</div>
}

interface ColumnProps {
  children?: React.ReactNode
}

/**
 " Column
 */
export function Column({ children }: ColumnProps) {
  return isChildrenEmpty(children) ? null : (
    <div className={styles.column}>{children}</div>
  )
}

interface ConfirmButtonProps {
  vm: SubmissionViewModel
}

/**
 " Confirm Button
 */
const ConfirmButton = observer(function ConfirmButton({
  vm,
}: ConfirmButtonProps) {
  return (
    <Button
      block
      type="primary"
      size="cta"
      disabled={!vm.canSubmit}
      loading={vm.submit.pending}
      onClick={vm.submit}
      data-test="draft-editor-submit-request-button"
    >
      Confirm Order
    </Button>
  )
})

interface InstructionsProps {
  vm: SubmissionViewModel
}

/**
 " Instructions
 */
const Instructions = observer(function Instructions({ vm }: InstructionsProps) {
  return (
    <div className={styles.instructions}>
      {vm.isAllowedToSubmit ? (
        <>
          Once you confirm your order and complete checkout, you will be
          redirected to your dashboard.
        </>
      ) : (
        <>
          Ready to submit but you are not allowed to do so. If you believe this
          is a mistake, please contact an administrator
        </>
      )}
    </div>
  )
})

interface SpacedProps {
  children?: React.ReactNode
}

/**
 " Spaced
 */
export function Spaced({ children }: SpacedProps) {
  return <div className={styles.spaced}>{children}</div>
}
